/**
 * Axios Authentication Plugin
 * 
 * This plugin intercepts all axios requests and adds the Authorization header
 * with the JWT token. It provides:
 * 1. Configured Nuxt $axios with auth headers
 * 2. A global $api instance (this.$api in components) with auth headers
 * 3. An exportable getAuthenticatedAxios() function for utils
 */

import Vue from 'vue';
import axios from 'axios';
import { API_BASE_URL } from '~/utils/constants';

// Store reference to the Nuxt app's $auth
let $auth = null;

/**
 * Get cookie value by name (fallback for when $auth is not available)
 */
function getCookie(name) {
  if (typeof document === 'undefined') return null;
  
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return decodeURIComponent(parts.pop().split(';').shift());
  }
  return null;
}

/**
 * Get the current auth token
 * Tries multiple sources: $auth module, cookies
 */
function getAuthToken() {
  // Try to get token from $auth module first
  if ($auth) {
    try {
      const token = $auth.strategy?.token?.get();
      if (token && token !== 'false') {
        return token;
      }
    } catch (e) {
      // Silent fail, will try cookie fallback
    }
  }
  
  // Fallback: get token from cookie (auth-next stores it here)
  const cookieToken = getCookie('auth._token.local');
  if (cookieToken && cookieToken !== 'false') {
    return cookieToken;
  }
  
  return null;
}

/**
 * Create an axios instance with auth interceptors
 */
function createAuthenticatedAxios() {
  const instance = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
    },
  });
  
  // Request interceptor - adds Authorization header
  instance.interceptors.request.use(
    (config) => {
      const token = getAuthToken();
      if (token) {
        config.headers = config.headers || {};
        config.headers['Authorization'] = token;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );
  
  // Response interceptor - handle auth and subscription errors
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        console.warn('Authentication error - token may be expired');
      }

      // Handle subscription-related errors (403)
      if (error.response?.status === 403) {
        const data = error.response.data;

        // Show upgrade modal for subscription errors
        if (data.error === 'upgrade_required' || data.error === 'limit_reached' || data.error === 'activation_required') {
          // Use global upgrade modal if available
          if (typeof window !== 'undefined' && window.$nuxt && window.$nuxt.$upgradeModal) {
            window.$nuxt.$upgradeModal.showModal(data);
          } else {
            // Fallback: show browser alert
            let message = data.message || 'Deze functie vereist een upgrade van uw abonnement.';
            if (data.upgrade_url) {
              message += '\n\nKlik OK om naar de upgrade pagina te gaan.';
              if (confirm(message)) {
                window.location.href = data.upgrade_url;
              }
            } else {
              alert(message);
            }
          }
        }
      }

      return Promise.reject(error);
    }
  );
  
  return instance;
}

// Create the authenticated axios instance
const authenticatedAxios = createAuthenticatedAxios();

// Export for use in utils/api.js
export { authenticatedAxios, getAuthToken };

/**
 * Get a fresh authenticated axios instance (useful if you need a new one)
 */
export function getAuthenticatedAxios() {
  return authenticatedAxios;
}

/**
 * Nuxt Plugin Export
 */
export default function (context, inject) {
  const { $axios } = context;
  
  // Store reference to $auth for token retrieval
  $auth = context.$auth;
  
  // Configure Nuxt's $axios with auth interceptors
  if ($axios) {
    $axios.onRequest((config) => {
      // Ensure headers object exists
      if (!config.headers) {
        config.headers = {};
      }
      const token = getAuthToken();
      if (token) {
        config.headers['Authorization'] = token;
      }
      return config;
    });
    
    $axios.onError((error) => {
      if (error.response?.status === 401) {
        console.warn('Authentication error on $axios request');
      }
      return Promise.reject(error);
    });
  }
  
  // Inject $api into Vue instances (this.$api) and Nuxt context
  // This allows components to use this.$api instead of require('axios')
  inject('api', authenticatedAxios);
  
  // Also add to Vue prototype for legacy support
  Vue.prototype.$apiClient = authenticatedAxios;
}
